import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { httpsCallable } from 'firebase/functions'
import { firebase_functions } from '../../../common/firebaseConfig'
import UpgradeDialog from '../../../components/UpgradeDialog'

import {
  WebinarInfoPanel,
  WebinarCategoryChips,
  WebinarType,
  WebinarSessionsResponse,
  WebinarSession,
  formatDateTime,
} from '../../../layout/sideNavigation/webinarCalendar/components'

import {
  LoadingView,
  ErrorView,
  EmptyView,
  SessionsView,
  CategoriesOnlyView,
  NoFilteredSessionsView,
} from '../../../layout/sideNavigation/webinarCalendar/local-components'

import { CategoryInfo } from '../../../layout/sideNavigation/webinarCalendar/types'
import {
  inferAccessibleTypesFromSessions,
  isCategoryAccessible,
  getFilteredSessions,
} from '../../../layout/sideNavigation/webinarCalendar/utils'

const WebinarTab: React.FC = () => {
  const [webinarSessions, setWebinarSessions] = useState<WebinarSession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [hasLoadedSessions, setHasLoadedSessions] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [allWebinarTypes, setAllWebinarTypes] = useState<WebinarType[]>([])
  const [accessibleWebinarTypes, setAccessibleWebinarTypes] = useState<string[]>([])
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false)
  const [selectedLockedCategory, setSelectedLockedCategory] = useState<string | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)

  const prevCategoryRef = useRef<string | null>(null)

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value)
  }

  useEffect(() => {
    if (prevCategoryRef.current !== selectedCategory) {
      prevCategoryRef.current = selectedCategory
      if (currentPage !== 1) {
        setCurrentPage(1)
      }
    }
  }, [currentPage, selectedCategory])

  const fetchWebinarSessions = useCallback(async () => {
    setIsLoadingSessions(true)
    setSessionError(null)

    try {
      const getUpcomingWebinarSessionsFn = httpsCallable(
        firebase_functions,
        'getUpcomingWebinarSessions'
      )
      const result = await getUpcomingWebinarSessionsFn({
        limit: 1000,
        offset: 0,
      })

      const response = result.data as WebinarSessionsResponse

      if (response.sessions) {
        setWebinarSessions(response.sessions)
        setHasLoadedSessions(true)

        if (response.allWebinarTypes && response.allWebinarTypes.length > 0) {
          setAllWebinarTypes(response.allWebinarTypes)
        }

        if (response.accessibleWebinarTypes && response.accessibleWebinarTypes.length > 0) {
          setAccessibleWebinarTypes(response.accessibleWebinarTypes)
        } else {
          const accessibleTypes = inferAccessibleTypesFromSessions(
            response.sessions,
            allWebinarTypes
          )
          setAccessibleWebinarTypes(accessibleTypes)
        }
      } else {
        setSessionError(response.message || 'Failed to fetch webinar sessions')
        setWebinarSessions([])
      }
    } catch (error: any) {
      setSessionError(error.message || 'An error occurred while fetching webinar sessions')
      setWebinarSessions([])
    } finally {
      setIsLoadingSessions(false)
    }
  }, [allWebinarTypes])

  useEffect(() => {
    if (!hasLoadedSessions && webinarSessions.length === 0) {
      void fetchWebinarSessions()
    }
  }, [hasLoadedSessions, webinarSessions.length, fetchWebinarSessions])

  const groupedWebinarTypes = useMemo(() => {
    const uniqueNicknames = new Set<string>()
    allWebinarTypes.forEach((type) => {
      uniqueNicknames.add(type.nickname)
    })

    const sortedNicknames = Array.from(uniqueNicknames).sort((a, b) => {
      const aAccessible = accessibleWebinarTypes.includes(a)
      const bAccessible = accessibleWebinarTypes.includes(b)

      if (aAccessible && !bAccessible) return -1
      if (!aAccessible && bAccessible) return 1
      return a.localeCompare(b)
    })

    return sortedNicknames
  }, [allWebinarTypes, accessibleWebinarTypes])

  const selectedCategoryInfo = useMemo<CategoryInfo | null>(() => {
    if (!selectedCategory) return null

    const matchingType = allWebinarTypes.find((type) => type.nickname === selectedCategory)

    if (matchingType) {
      return {
        description: matchingType.description,
        schedule: matchingType.schedule,
      }
    }

    return null
  }, [selectedCategory, allWebinarTypes])

  const handleRegistrationSuccess = () => {
    void fetchWebinarSessions()
  }

  const filteredSessions = useMemo(() => {
    return getFilteredSessions(webinarSessions, selectedCategory, allWebinarTypes)
  }, [webinarSessions, selectedCategory, allWebinarTypes])

  const checkCategoryAccess = (category: string) => {
    return isCategoryAccessible(category, accessibleWebinarTypes, webinarSessions, allWebinarTypes)
  }

  const handleLockedCategoryClick = (category: string) => {
    setSelectedLockedCategory(category)
    setUpgradeDialogOpen(true)
  }

  const getMarketplaceSection = useMemo(() => {
    if (!selectedLockedCategory) return 'membership'

    if (selectedLockedCategory.includes('Mastermind')) {
      return 'Personal%20Coaching'
    }

    return 'membership'
  }, [selectedLockedCategory])

  const renderContent = () => {
    if (isLoadingSessions) {
      return <LoadingView isLoading={isLoadingSessions} />
    }

    if (sessionError) {
      return <ErrorView error={sessionError} onRetry={fetchWebinarSessions} />
    }

    if (webinarSessions.length === 0 && groupedWebinarTypes.length === 0) {
      return <EmptyView />
    }

    if (webinarSessions.length === 0) {
      return (
        <CategoriesOnlyView
          selectedCategory={selectedCategory}
          isCategoryAccessible={checkCategoryAccess}
          categoryInfo={selectedCategoryInfo}
          webinarTypes={allWebinarTypes}
          onRegistrationSuccess={handleRegistrationSuccess}
          sessions={webinarSessions}
        />
      )
    }

    if (filteredSessions.length === 0) {
      return (
        <NoFilteredSessionsView
          selectedCategory={selectedCategory}
          isCategoryAccessible={checkCategoryAccess}
          categoryInfo={selectedCategoryInfo}
          webinarTypes={allWebinarTypes}
          onRegistrationSuccess={handleRegistrationSuccess}
          sessions={webinarSessions}
        />
      )
    }

    return (
      <SessionsView
        sessions={filteredSessions}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        isAllTab={selectedCategory === null}
        allWebinarTypes={allWebinarTypes}
      />
    )
  }

  return (
    <>
      {!isLoadingSessions && !sessionError && groupedWebinarTypes.length > 0 && (
        <WebinarCategoryChips
          selectedCategory={selectedCategory}
          groupedWebinarTypes={groupedWebinarTypes}
          isCategoryAccessible={checkCategoryAccess}
          onCategorySelect={setSelectedCategory}
          onLockedCategoryClick={handleLockedCategoryClick}
        />
      )}

      {!isLoadingSessions &&
        selectedCategory &&
        selectedCategoryInfo &&
        !(filteredSessions.length === 0 && checkCategoryAccess(selectedCategory)) && (
          <WebinarInfoPanel
            categoryName={selectedCategory}
            description={selectedCategoryInfo.description}
            schedule={selectedCategoryInfo.schedule}
            sessions={filteredSessions}
            formatDateTime={formatDateTime}
          />
        )}

      {renderContent()}

      <UpgradeDialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        marketplaceSection={getMarketplaceSection}
      />
    </>
  )
}

export default WebinarTab
